import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles, Building2, MapPin, ArrowRight, ArrowLeft, Plus, Trash2,
  Check, Briefcase, Loader2,
} from "lucide-react";

const ROLE_PRESETS = [
  "Director of Nursing",
  "Chief Nursing Officer",
  "Quality Director",
  "Infection Preventionist",
  "Risk Manager",
  "Patient Safety Officer",
  "VP of Clinical Operations",
  "Clinical Manager",
  "Other",
];

const COLOR_PALETTE = [
  { hex: "#7c3aed", name: "Violet" },
  { hex: "#3B82F6", name: "Blue" },
  { hex: "#10B981", name: "Emerald" },
  { hex: "#F59E0B", name: "Amber" },
  { hex: "#EF4444", name: "Red" },
  { hex: "#EC4899", name: "Pink" },
  { hex: "#06B6D4", name: "Cyan" },
  { hex: "#8B5CF6", name: "Purple" },
];

const DEFAULT_UNIT_PRESETS = [
  { name: "Unit 200", description: "Detox" },
  { name: "Unit 300", description: "Adult" },
  { name: "Unit 400", description: "Adult High Acuity" },
  { name: "Unit 500", description: "Adolescent" },
  { name: "Unit 600", description: "Adolescent" },
];

export default function SetupWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [titleCustom, setTitleCustom] = useState("");
  const [hospital, setHospital] = useState("");
  const [shortName, setShortName] = useState("");
  const [color, setColor] = useState(COLOR_PALETTE[0].hex);
  const [units, setUnits] = useState([{ name: "", description: "" }]);

  // On mount: redirect if already complete
  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/setup/status`, { withCredentials: true });
        if (!res.data?.needs_wizard) {
          navigate("/dashboard", { replace: true });
          return;
        }
        if (res.data?.title) setTitle(res.data.title);
        setChecking(false);
      } catch (_) {
        navigate("/login", { replace: true });
      }
    };
    check();
  }, [navigate]);

  // Auto-derive short_name from hospital first letters
  useEffect(() => {
    if (!shortName && hospital) {
      const auto = hospital
        .split(/\s+/)
        .filter(w => w.length > 0)
        .map(w => w[0].toUpperCase())
        .join("")
        .slice(0, 6);
      setShortName(auto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospital]);

  const effectiveTitle = title === "Other" ? titleCustom.trim() : title.trim();
  const filledUnits = units.filter(u => u.name.trim().length > 0);

  const addUnit = () => setUnits([...units, { name: "", description: "" }]);
  const removeUnit = (idx) => setUnits(units.filter((_, i) => i !== idx));
  const updateUnit = (idx, field, value) => {
    const next = [...units];
    next[idx] = { ...next[idx], [field]: value };
    setUnits(next);
  };
  const useUnitPresets = () => setUnits(DEFAULT_UNIT_PRESETS.map(u => ({ ...u })));

  const canAdvance = () => {
    if (step === 0) return effectiveTitle.length > 0;
    if (step === 1) return hospital.trim().length > 0 && shortName.trim().length > 0;
    if (step === 2) return true; // units optional
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Save title (best-effort) — main wizard call also persists it.
      await axios.post(
        `${API}/setup/complete`,
        {
          title: effectiveTitle || null,
          facility: {
            name: hospital.trim(),
            short_name: shortName.trim(),
            role: effectiveTitle || "Director",
            color,
            units: filledUnits.map(u => ({
              name: u.name.trim(),
              description: u.description?.trim() || null,
            })),
          },
        },
        { withCredentials: true },
      );
      toast.success("You're all set. Welcome to Niko OS.");
      // Force a fresh reload so all hooks pull the new data
      window.location.assign("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your setup");
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950
                    flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-2xl">
        {/* Progress strip */}
        <div className="mb-6 flex items-center gap-2">
          {[0, 1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                s <= step ? "bg-violet-500" : "bg-zinc-800"
              }`}
            />
          ))}
        </div>

        <div className="bg-zinc-900/80 backdrop-blur border border-zinc-800 rounded-2xl
                        shadow-2xl p-8 space-y-6">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500
                            flex items-center justify-center text-white font-bold shadow-lg">
              N
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-violet-400">
                Niko OS · Setup
              </div>
              <div className="text-sm text-zinc-400">
                Step {step + 1} of 4
              </div>
            </div>
          </div>

          {/* ============ STEP 0: TITLE ============ */}
          {step === 0 && (
            <div className="space-y-5" data-testid="setup-step-title">
              <div>
                <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                  <Sparkles className="w-7 h-7 text-violet-400" />
                  What's your title?
                </h1>
                <p className="text-sm text-zinc-400 mt-2">
                  This shows up on your morning brief, planner exports, and shared docs.
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ROLE_PRESETS.map(r => (
                  <button
                    key={r}
                    onClick={() => setTitle(r)}
                    className={`px-3 py-2.5 text-sm rounded-lg border text-left transition-colors
                                ${title === r
                                  ? "bg-violet-500/15 border-violet-500 text-white"
                                  : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}
                    data-testid={`role-preset-${r.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {r}
                  </button>
                ))}
              </div>

              {title === "Other" && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Your title</Label>
                  <Input
                    value={titleCustom}
                    onChange={(e) => setTitleCustom(e.target.value)}
                    placeholder="e.g. Associate Chief Quality Officer"
                    autoFocus
                    data-testid="role-custom-input"
                  />
                </div>
              )}
            </div>
          )}

          {/* ============ STEP 1: HOSPITAL ============ */}
          {step === 1 && (
            <div className="space-y-5" data-testid="setup-step-hospital">
              <div>
                <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                  <Building2 className="w-7 h-7 text-violet-400" />
                  Where do you work?
                </h1>
                <p className="text-sm text-zinc-400 mt-2">
                  Your primary facility. You can add more later.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Hospital / Facility name</Label>
                  <Input
                    value={hospital}
                    onChange={(e) => setHospital(e.target.value)}
                    placeholder="e.g. Saint Mary's Behavioral Health"
                    autoFocus
                    data-testid="hospital-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">
                    Short name <span className="text-zinc-500">(used in compact views)</span>
                  </Label>
                  <Input
                    value={shortName}
                    onChange={(e) => setShortName(e.target.value.toUpperCase().slice(0, 8))}
                    placeholder="SMBH"
                    maxLength={8}
                    className="font-mono uppercase tracking-widest"
                    data-testid="hospital-short-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Color (for calendar + planner)</Label>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_PALETTE.map(c => (
                      <button
                        key={c.hex}
                        onClick={() => setColor(c.hex)}
                        className={`w-10 h-10 rounded-lg border-2 transition-transform hover:scale-110
                                    ${color === c.hex ? "border-white ring-2 ring-violet-400" : "border-zinc-700"}`}
                        style={{ backgroundColor: c.hex }}
                        title={c.name}
                        aria-label={`Pick ${c.name}`}
                        data-testid={`color-${c.name.toLowerCase()}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============ STEP 2: UNITS ============ */}
          {step === 2 && (
            <div className="space-y-5" data-testid="setup-step-units">
              <div>
                <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                  <MapPin className="w-7 h-7 text-violet-400" />
                  Your units for rounding
                </h1>
                <p className="text-sm text-zinc-400 mt-2">
                  Add the units you intentionally round on. You can edit these
                  any time from the facility menu.
                </p>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={useUnitPresets}
                  data-testid="use-presets-btn"
                  className="text-xs"
                >
                  Use psychiatric defaults (Unit 200/300/400/500/600)
                </Button>
              </div>

              <div className="space-y-2">
                {units.map((u, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <Input
                      value={u.name}
                      onChange={(e) => updateUnit(idx, "name", e.target.value)}
                      placeholder="Unit name (e.g. Unit 300)"
                      className="flex-1"
                      data-testid={`unit-name-${idx}`}
                    />
                    <Input
                      value={u.description}
                      onChange={(e) => updateUnit(idx, "description", e.target.value)}
                      placeholder="Description (e.g. Adult Detox)"
                      className="flex-1"
                      data-testid={`unit-desc-${idx}`}
                    />
                    {units.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeUnit(idx)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0"
                        aria-label="Remove this unit"
                        data-testid={`remove-unit-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={addUnit}
                data-testid="add-unit-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add unit
              </Button>
            </div>
          )}

          {/* ============ STEP 3: REVIEW ============ */}
          {step === 3 && (
            <div className="space-y-5" data-testid="setup-step-review">
              <div>
                <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                  <Check className="w-7 h-7 text-emerald-400" />
                  Quick review
                </h1>
                <p className="text-sm text-zinc-400 mt-2">
                  Make sure this looks right — you can change everything later from
                  the facility menu and your profile.
                </p>
              </div>

              <div className="space-y-3">
                <ReviewRow icon={<Briefcase className="w-4 h-4" />} label="Title" value={effectiveTitle || "—"} />
                <ReviewRow
                  icon={<Building2 className="w-4 h-4" />}
                  label="Hospital"
                  value={`${hospital} (${shortName})`}
                  trailing={
                    <span
                      className="w-4 h-4 rounded-full inline-block border border-zinc-700"
                      style={{ backgroundColor: color }}
                    />
                  }
                />
                <ReviewRow
                  icon={<MapPin className="w-4 h-4" />}
                  label={`Rounding units (${filledUnits.length})`}
                  value={
                    filledUnits.length === 0
                      ? "None — you can add units later"
                      : filledUnits.map(u => u.name + (u.description ? ` (${u.description})` : "")).join(", ")
                  }
                />
              </div>
            </div>
          )}

          {/* Nav */}
          <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
            <Button
              variant="ghost"
              disabled={step === 0 || submitting}
              onClick={() => setStep(s => Math.max(0, s - 1))}
              data-testid="wizard-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            {step < 3 ? (
              <Button
                onClick={() => setStep(s => s + 1)}
                disabled={!canAdvance()}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="wizard-next-btn"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="wizard-finish-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Finish & open dashboard
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-zinc-600 mt-6">
          Niko OS · Director's Operating System · Closed beta
        </p>
      </div>
    </div>
  );
}

function ReviewRow({ icon, label, value, trailing }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/40 border border-zinc-800">
      <div className="text-violet-400 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
        <div className="text-sm text-white truncate">{value}</div>
      </div>
      {trailing}
    </div>
  );
}
